import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Download, LogIn, LogOut, ShieldAlert, Users } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ADMIN_T = {
  it: {
    title: "Area iscritti",
    sub: "Tutte le persone in waiting list, in tempo reale.",
    loading: "Caricamento...",
    loginPrompt: "Accedi con Google per vedere la lista degli iscritti.",
    loginBtn: "Accedi",
    denied: "Quest'area è riservata al team Syncroo.",
    total: (n) => `${n} ${n === 1 ? "iscritto" : "iscritti"}`,
    name: "Nome",
    city: "Città",
    email: "Email",
    motivation: "Motivazione",
    lang: "Lingua",
    date: "Data",
    account: "Account",
    csv: "Esporta CSV",
    back: "Torna alla home",
    empty: "Ancora nessun iscritto: la prima persona in sync arriverà presto.",
    error: "Errore nel caricamento. Riprova.",
  },
  en: {
    title: "Signups area",
    sub: "Everyone on the waitlist, in real time.",
    loading: "Loading...",
    loginPrompt: "Sign in with Google to see the waitlist.",
    loginBtn: "Sign in",
    denied: "This area is reserved to the Syncroo team.",
    total: (n) => `${n} ${n === 1 ? "signup" : "signups"}`,
    name: "Name",
    city: "City",
    email: "Email",
    motivation: "Motivation",
    lang: "Lang",
    date: "Date",
    account: "Account",
    csv: "Export CSV",
    back: "Back to home",
    empty: "No signups yet: the first person in sync will arrive soon.",
    error: "Loading failed. Try again.",
  },
};

export default function AdminArea({ locale, setLocale, user, authChecked, onLogin, onLogout }) {
  const t = ADMIN_T[locale] || ADMIN_T.it;
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) return;
    fetch(`${API}/api/admin/waitlist`, { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error("forbidden");
        return res.json();
      })
      .then(setData)
      .catch(() => setError(true));
  }, [user]);

  const exportCsv = () => {
    if (!data) return;
    const header = ["email", "name", "city", "motivation", "locale", "created_at"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = data.entries.map((e) => header.map((k) => esc(e[k])).join(","));
    const blob = new Blob([[header.join(","), ...rows].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "syncroo-waitlist.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const fmtDate = (d) =>
    new Date(d).toLocaleString(locale === "it" ? "it-IT" : "en-GB", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <div data-testid="admin-section" className="min-h-screen bg-void font-body text-bone antialiased">
      <header className="border-b border-black/10 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <a href="/" data-testid="admin-logo" className="flex items-center gap-2.5">
            <img src="/logo-mark.png" alt="Logo Syncroo" className="h-8 w-8 object-contain" />
            <span className="font-display text-lg font-black uppercase tracking-tight text-flame">Syncroo</span>
          </a>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest">
              <button
                data-testid="admin-lang-it-btn"
                onClick={() => setLocale("it")}
                className={locale === "it" ? "text-bone" : "text-ash hover:text-bone"}
              >
                IT
              </button>
              <span className="text-ash/40">/</span>
              <button
                data-testid="admin-lang-en-btn"
                onClick={() => setLocale("en")}
                className={locale === "en" ? "text-bone" : "text-ash hover:text-bone"}
              >
                EN
              </button>
            </div>
            {user && (
              <button
                data-testid="admin-logout-btn"
                onClick={onLogout}
                className="inline-flex items-center gap-1.5 font-display text-xs font-bold uppercase tracking-widest text-ash transition-colors duration-300 hover:text-flame"
              >
                <LogOut size={14} />
                <span className="hidden sm:inline">{t.account}: {user.email}</span>
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-16 md:py-24">
        {!authChecked ? (
          <p data-testid="admin-loading" className="font-display text-sm uppercase tracking-widest text-ash">
            {t.loading}
          </p>
        ) : !user ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-start gap-6 border border-black/10 bg-white p-10"
            data-testid="admin-login-prompt"
          >
            <LogIn size={32} className="text-flame" />
            <p className="text-base text-ash md:text-lg">{t.loginPrompt}</p>
            <button
              data-testid="admin-login-btn"
              onClick={onLogin}
              className="rounded-full bg-flame px-8 py-3.5 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-flame-hover"
            >
              {t.loginBtn}
            </button>
          </motion.div>
        ) : !user.is_admin ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-start gap-6 border border-black/10 bg-white p-10"
            data-testid="admin-denied"
          >
            <ShieldAlert size={32} className="text-flame" />
            <p className="text-base text-ash md:text-lg">{t.denied}</p>
            <a href="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest text-flame">
              <ArrowLeft size={14} /> {t.back}
            </a>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
              <div>
                <h1 data-testid="admin-title" className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
                  {t.title}
                </h1>
                <p className="mt-3 text-base text-ash md:text-lg">{t.sub}</p>
              </div>
              <div className="flex items-center gap-4">
                <span
                  data-testid="admin-count"
                  className="inline-flex items-center gap-2 border border-black/15 bg-white px-5 py-3 font-display text-sm font-bold uppercase tracking-widest"
                >
                  <Users size={16} className="text-flame" />
                  {data ? t.total(data.count) : "…"}
                </span>
                <button
                  data-testid="admin-csv-btn"
                  onClick={exportCsv}
                  disabled={!data || data.count === 0}
                  className="inline-flex items-center gap-2 rounded-full bg-flame px-6 py-3 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-flame-hover disabled:opacity-50"
                >
                  <Download size={16} /> {t.csv}
                </button>
              </div>
            </div>

            {error ? (
              <p data-testid="admin-error" className="text-sm text-red-500">{t.error}</p>
            ) : data && data.count === 0 ? (
              <p data-testid="admin-empty" className="border border-black/10 bg-white p-10 text-base text-ash">
                {t.empty}
              </p>
            ) : (
              <div className="overflow-x-auto border border-black/10 bg-white" data-testid="admin-table-wrap">
                <table data-testid="admin-table" className="w-full min-w-[760px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-black/10 font-display text-xs font-bold uppercase tracking-widest text-ash">
                      <th className="px-5 py-4">{t.email}</th>
                      <th className="px-5 py-4">{t.name}</th>
                      <th className="px-5 py-4">{t.city}</th>
                      <th className="px-5 py-4">{t.motivation}</th>
                      <th className="px-5 py-4">{t.date}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.entries || []).map((e) => (
                      <tr key={e.id} data-testid={`admin-row-${e.id}`} className="border-b border-black/5 last:border-0 hover:bg-void/60">
                        <td className="px-5 py-4 font-medium">{e.email}</td>
                        <td className="px-5 py-4 text-ash">{e.name}</td>
                        <td className="px-5 py-4 text-ash">{e.city || "—"}</td>
                        <td className="max-w-[320px] truncate px-5 py-4 text-ash" title={e.motivation}>
                          {e.motivation}
                        </td>
                        <td className="px-5 py-4 text-ash">{fmtDate(e.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <a href="/" data-testid="admin-back-home" className="mt-10 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest text-ash transition-colors duration-300 hover:text-flame">
              <ArrowLeft size={14} /> {t.back}
            </a>
          </motion.div>
        )}
      </main>
    </div>
  );
}
